import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useLang } from '../../contexts/LangContext';
import { getTaskById, updateTask, addTaskUpdate } from '../../api/tasksApi';
import { Task, TaskStatus, UpdateTaskRequest, TaskAttachment } from '../../types';
import { ImageModal } from '../../components/ImageModal';

const TaskDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLang();

  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateText, setUpdateText] = useState('');
  const [isCompleted, setIsCompleted] = useState(false);
  const [markInProgress, setMarkInProgress] = useState(false);
  const [priority, setPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'>('MEDIUM');
  const [viewingFile, setViewingFile] = useState<TaskAttachment | null>(null);
  const [updateFiles, setUpdateFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  
  // Image modal state for enhanced image viewing
  const [imageModal, setImageModal] = useState<{
    isOpen: boolean;
    fileName: string;
    fileUrl: string;
  }>({
    isOpen: false,
    fileName: '',
    fileUrl: ''
  });

  useEffect(() => {
    if (id) {
      loadTask();
    }
  }, [id]);

  const loadTask = async () => {
    try {
      setLoading(true);
      const data = await getTaskById(id!);
      setTask(data);
      setIsCompleted(data.status === TaskStatus.COMPLETED);
      setMarkInProgress(false);
      if ((data as any).priority) setPriority((data as any).priority);
    } catch (error) {
      console.error('Error loading task:', error);
      toast.error(t('errorLoadingTask') || 'Có lỗi xảy ra khi tải công việc');
      navigate('/tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length + updateFiles.length > 2) {
      toast.error(t('maxTwoFiles') || 'Maximum 2 files allowed');
      return;
    }
    setUpdateFiles(prev => [...prev, ...selectedFiles]);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length + updateFiles.length > 2) {
      toast.error(t('maxTwoFiles') || 'Maximum 2 files allowed');
      return;
    }
    setUpdateFiles(prev => [...prev, ...droppedFiles]);
  };

  const removeUpdateFile = (index: number) => {
    setUpdateFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleStatusUpdate = async () => {
    if (!task) return;

    setUpdateLoading(true);
    try {
      await addTaskUpdate(task.id, {
        content: updateText.trim() || (task.status === TaskStatus.PENDING ? (t('confirmInProgress') || 'Xác nhận đang thực hiện') : (t('confirmCompleted') || 'Xác nhận đã thực hiện xong')),
        markCompleted: isCompleted,
        // Pass markInProgress to prevent auto-progress when unchecked
        // @ts-ignore
        markInProgress: markInProgress,
      } as any, updateFiles);
      // Build optional status change based on user selection
      let newStatus: TaskStatus | undefined = undefined;
      if (task.status === TaskStatus.PENDING && markInProgress) newStatus = TaskStatus.IN_PROGRESS;
      if (task.status === TaskStatus.IN_PROGRESS && isCompleted) newStatus = TaskStatus.COMPLETED;

      // Persist status/priority only if changed
      if ((task as any).priority !== priority || newStatus) {
        await updateTask(task.id, { content: task.description, priority, status: newStatus });
      }
      toast.success(t('taskUpdatedSuccessfully') || 'Cập nhật công việc thành công');
      loadTask(); // Reload task to get updated data
      setUpdateText('');
      setUpdateFiles([]);
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error(t('errorUpdatingTask') || 'Có lỗi xảy ra khi cập nhật công việc');
    } finally {
      setUpdateLoading(false);
    }
  };

  const getStatusBadge = (status: TaskStatus) => {
    const statusClasses = {
      [TaskStatus.PENDING]: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      [TaskStatus.IN_PROGRESS]: 'bg-blue-100 text-blue-800 border border-blue-200',
      [TaskStatus.COMPLETED]: 'bg-green-100 text-green-800 border border-green-200',
      [TaskStatus.CANCELLED]: 'bg-red-100 text-red-800 border border-red-200',
    };

    const statusTexts = {
      [TaskStatus.PENDING]: t('taskStatusPending') || 'Chưa hoàn thành',
      [TaskStatus.IN_PROGRESS]: t('taskStatusInProgress') || 'Đang thực hiện',
      [TaskStatus.COMPLETED]: t('taskStatusCompleted') || 'Đã hoàn thành',
      [TaskStatus.CANCELLED]: t('cancelled') || 'Đã hủy',
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusClasses[status]}`}>
        {statusTexts[status]}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getViewUrl = (attachment: TaskAttachment) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
    return `${API_BASE_URL}/tasks/attachments/${attachment.id}/view`;
  };

  const getDownloadUrl = (attachment: TaskAttachment) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
    return `${API_BASE_URL}/tasks/attachments/${attachment.id}/download`;
  };

  const downloadAttachment = async (attachment: TaskAttachment) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getDownloadUrl(attachment), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      // Create blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.fileName;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Không thể tải file. Vui lòng thử lại.');
    }
  };

  const viewFile = async (attachment: TaskAttachment) => {
    if (isImageFile(attachment)) {
      // Use enhanced ImageModal for images
      try {
        const token = localStorage.getItem('token');
        const viewUrl = `${getViewUrl(attachment)}?token=${token || ''}`;
        setImageModal({
          isOpen: true,
          fileName: attachment.fileName,
          fileUrl: viewUrl
        });
      } catch (error) {
        console.error('Error setting up image modal:', error);
        // Fallback to existing modal
        setViewingFile(attachment);
      }
    } else {
      // Use existing modal for non-image files
      setViewingFile(attachment);
    }
  };

  const getGoogleDocsViewerUrl = (attachment: TaskAttachment) => {
    const token = localStorage.getItem('token');
    const viewUrl = `${getViewUrl(attachment)}?token=${token}`;
    return `https://docs.google.com/viewer?url=${encodeURIComponent(viewUrl)}&embedded=true`;
  };

  const getAuthenticatedImageUrl = (attachment: TaskAttachment) => {
    const token = localStorage.getItem('token');
    return `${getViewUrl(attachment)}?token=${token || ''}`;
  };

  const canViewFile = (attachment: TaskAttachment) => {
    const viewableMimeTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/bmp',
      'image/webp'
    ];
    return viewableMimeTypes.includes(attachment.mimeType);
  };

  const isImageFile = (attachment: TaskAttachment) => {
    return attachment.mimeType.startsWith('image/');
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return (
        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    } else if (mimeType === 'application/pdf') {
      return (
        <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    } else if (mimeType.includes('word') || mimeType.includes('document')) {
      return (
        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    } else if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {
      return (
        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
        </svg>
      );
    } else {
      return (
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
        </svg>
      );
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">{t('loading') || 'Đang tải...'}</p>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('taskNotFound') || 'Không tìm thấy công việc'}</h2>
          <button
            onClick={() => navigate('/tasks')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            {t('backToTasks') || 'Quay lại danh sách'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {t('taskDetails') || 'Chi tiết công việc'}
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                {t('viewAndUpdateTaskDetails') || 'Xem và cập nhật thông tin chi tiết công việc'}
              </p>
            </div>
            <button
              onClick={() => navigate('/tasks')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {t('back') || 'Quay lại'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Task Requirements */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <h2 className="text-lg font-semibold text-white flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('taskRequirements') || 'Nội dung yêu cầu'}
              </h2>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {/* Task Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('taskTitle') || 'Tiêu đề'}
                  </label>
                  <p className="text-gray-900 font-medium text-lg">{task.title}</p>
                </div>

                {/* Status & Priority */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('status') || 'Trạng thái'}
                  </label>
                  <div>{getStatusBadge(task.status)}</div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mức độ ưu tiên
                  </label>
                  <select
                    className="w-full px-3 py-2 border rounded-md"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value as any)}
                  >
                    <option value="LOW">Thấp</option>
                    <option value="MEDIUM">Trung bình</option>
                    <option value="HIGH">Cao</option>
                    <option value="URGENT">Khẩn cấp</option>
                  </select>
                </div>

                {/* Creator and Created Date - Same Line */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('createdBy') || 'Người tạo'}
                    </label>
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-sm font-medium text-green-600">
                          {task.createdBy?.name?.charAt(0) || 'N'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-900 font-medium block">{task.createdBy?.name || 'N/A'}</span>
                        <span className="text-sm text-gray-500">({task.createdBy?.email || 'unknown'})</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('createdDate') || 'Ngày tạo'}
                    </label>
                    <p className="text-gray-900 font-medium">{formatDate(task.createdAt)}</p>
                  </div>
                </div>

                {/* Assigned User and Due Date - Same Line */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('assignedTo') || 'Người được giao'}
                    </label>
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-sm font-medium text-blue-600">
                          {task.assignedTo?.name?.charAt(0) || 'N'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-900 font-medium block">{task.assignedTo?.name || 'N/A'}</span>
                        <span className="text-sm text-gray-500">({task.assignedTo?.email || 'unknown'})</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('dueDate') || 'Hạn hoàn thành'}
                    </label>
                    <p className="text-gray-900 font-medium text-red-600">{formatDate(task.dueDate)}</p>
                  </div>
                </div>

                {/* Completed Date (if exists) */}
                {task.completedAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('completedDate') || 'Ngày hoàn thành'}
                    </label>
                    <p className="text-gray-900 text-green-600 font-medium">{formatDate(task.completedAt)}</p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t('description') || 'Mô tả công việc'}
                </label>
                <div 
                  className="prose max-w-none text-gray-700 bg-gray-50 p-4 rounded-lg border border-gray-200"
                  dangerouslySetInnerHTML={{ __html: task.description }}
                />
              </div>

              {/* File Attachments */}
              {task.attachments && task.attachments.filter((a: any) => !a.updateId).length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {t('attachments') || 'File đính kèm'}
                  </label>
                  <div className="space-y-3">
                    {task.attachments.filter((a: any) => !a.updateId).map((attachment, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                        <div className="flex items-center flex-1 min-w-0">
                          {getFileIcon(attachment.mimeType)}
                          <div className="ml-3 flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {attachment.fileName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(attachment.fileSize)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          {canViewFile(attachment) && (
                            <button
                              onClick={() => viewFile(attachment)}
                              className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                            >
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              Xem
                            </button>
                          )}
                          <button
                            onClick={() => downloadAttachment(attachment)}
                            className="inline-flex items-center px-3 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors"
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Tải về
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Updates and Feedback */}
          <div className="space-y-6">
            {/* Update Form */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4">
                <h2 className="text-lg font-semibold text-white flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  {t('updatesAndFeedback') || 'Cập nhật & phản hồi'}
                </h2>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      {t('addUpdate') || 'Thêm cập nhật'}
                    </label>
                    <textarea
                      value={updateText}
                      onChange={(e) => setUpdateText(e.target.value)}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder={t('enterUpdateComment') || 'Nhập nội dung cập nhật, ghi chú tiến độ...'}
                    />
                  </div>

                  {/* File Upload Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      {t('attachFiles') || 'Đính kèm tệp'}
                    </label>
                    
                    {/* File Upload Area */}
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                        dragActive
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                    >
                      <div className="space-y-2">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="text-sm text-gray-600">
                          <label
                            htmlFor="file-upload"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                          >
                            <span>{t('uploadFiles') || 'Tải lên tệp'}</span>
                            <input
                              id="file-upload"
                              name="file-upload"
                              type="file"
                              multiple
                              className="sr-only"
                              onChange={handleFileChange}
                              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                            />
                          </label>
                          <p className="pl-1">{t('orDragAndDrop') || 'hoặc kéo và thả'}</p>
                        </div>
                        <p className="text-xs text-gray-500">
                          {t('maxTwoFiles') || 'Tối đa 2 tệp'} • {t('maxFileSize') || 'Tối đa 10MB mỗi tệp'}
                        </p>
                      </div>
                    </div>

                    {/* Selected Files */}
                    {updateFiles.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <h4 className="text-sm font-medium text-gray-700">
                          {t('selectedFiles') || 'Tệp đã chọn'} ({updateFiles.length}/2)
                        </h4>
                        {updateFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                          >
                            <div className="flex items-center space-x-3">
                              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                <p className="text-xs text-gray-500">
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeUpdateFile(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {task.status === TaskStatus.PENDING && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="inprogress"
                          checked={markInProgress}
                          onChange={(e) => setMarkInProgress(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="inprogress" className="ml-3 text-sm font-medium text-blue-900">
                          {t('confirmInProgress') || 'Xác nhận đang thực hiện'}
                        </label>
                      </div>
                    </div>
                  )}

                  {task.status === TaskStatus.IN_PROGRESS && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="completed"
                          checked={isCompleted}
                          onChange={(e) => setIsCompleted(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="completed" className="ml-3 text-sm font-medium text-blue-900">
                          {t('confirmCompleted') || 'Xác nhận đã thực hiện xong'}
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      onClick={handleStatusUpdate}
                      disabled={updateLoading}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {updateLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          {t('updating') || 'Đang cập nhật...'}
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                          {t('updateTask') || 'Cập nhật công việc'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Task History */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('taskHistory') || 'Lịch sử công việc'}
                </h3>
              </div>
              
              <div className="p-6">
                {task.updates && task.updates.length > 0 ? (
                  <div className="space-y-4">
                    {task.updates.map((update, index) => (
                      <div key={index} className="relative pl-6 pb-4">
                        {index !== task.updates!.length - 1 && (
                          <div className="absolute left-2 top-8 bottom-0 w-0.5 bg-gray-200"></div>
                        )}
                        <div className="absolute left-0 top-2 w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow"></div>
                        
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                                <span className="text-xs font-medium text-blue-600">
                                  {update.createdBy?.name?.charAt(0) || 'N'}
                                </span>
                              </div>
                              <span className="text-sm font-medium text-gray-900">
                                {update.createdBy?.name || t('unknown') || 'Không rõ'}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-md border">
                              {formatDate(update.createdAt)}
                            </span>
                          </div>
                          
                          {update.content && (
                            <p className="text-sm text-gray-700 mb-3 leading-relaxed">{update.content}</p>
                          )}

                          {update.attachments && update.attachments.length > 0 && (
                            <div className="mt-2 space-y-2">
                              {update.attachments.map((attachment) => (
                                <div key={attachment.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                                  <div className="flex items-center flex-1 min-w-0">
                                    {getFileIcon(attachment.mimeType)}
                                    <div className="ml-3 flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 truncate">{attachment.fileName}</p>
                                      <p className="text-xs text-gray-500">{formatFileSize(attachment.fileSize)}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2 ml-4">
                                    {canViewFile(attachment) && (
                                      <button
                                        onClick={() => viewFile(attachment)}
                                        className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                                      >
                                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        Xem
                                      </button>
                                    )}
                                    <button
                                      onClick={() => downloadAttachment(attachment)}
                                      className="inline-flex items-center px-3 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors"
                                    >
                                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                      Tải về
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {update.isCompleted && (
                            <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              {t('taskCompleted') || 'Đã hoàn thành'}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">{t('noUpdatesYet') || 'Chưa có cập nhật nào'}</h3>
                    <p className="mt-1 text-sm text-gray-500">Hãy thêm cập nhật đầu tiên cho công việc này.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* File Viewer Modal */}
      {viewingFile && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50">
          <div className="w-full h-full flex flex-col">
            <div className="flex items-center justify-between p-3 bg-black/60 text-white">
              <div className="flex items-center">
                {getFileIcon(viewingFile.mimeType)}
                <div className="ml-3">
                  <h3 className="text-base font-medium">{viewingFile.fileName}</h3>
                  <p className="text-xs opacity-80">{formatFileSize(viewingFile.fileSize)}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => downloadAttachment(viewingFile)}
                  className="inline-flex items-center px-3 py-1 border border-white/30 shadow-sm text-xs leading-4 font-medium rounded-md text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Tải về
                </button>
                <button
                  onClick={() => setViewingFile(null)}
                  className="inline-flex items-center p-2 border border-transparent rounded-md text-white hover:text-white hover:bg-white/10 focus:outline-none"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex-1 relative bg-black">
              {isImageFile(viewingFile) ? (
                  <img
                    src={getAuthenticatedImageUrl(viewingFile)}
                    alt={viewingFile.fileName}
                  className="absolute inset-0 w-full h-full object-contain"
                  />
              ) : (
                <iframe
                  src={getGoogleDocsViewerUrl(viewingFile)}
                    className="absolute inset-0 w-full h-full border-0 bg-white"
                  title={`Preview of ${viewingFile.fileName}`}
                />
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Enhanced Image Modal for Images */}
      <ImageModal
        isOpen={imageModal.isOpen}
        onClose={() => setImageModal({ isOpen: false, fileName: '', fileUrl: '' })}
        fileName={imageModal.fileName}
        fileUrl={imageModal.fileUrl}
      />
    </div>
  );
};

export default TaskDetails; 